import React, { useState, useEffect } from 'react';
import apiClient from '../../api/api';
import './CryptoPaymentForm.css';
import img from '../../binance-pay.png';
import { db, auth } from '../../Firebase';
import { doc, setDoc } from 'firebase/firestore';

const CryptoPaymentForm = ({ planType, billingPeriod }) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // Calculate amount based on plan type and billing period (same logic as Stripe)
  const calculateAmount = () => {
    let amount = 0;
    if (planType === 'enterprise') {
      if (billingPeriod === 'annual') {
        amount = 9999.00; // €9999.00
      } else if (billingPeriod === 'monthly') {
        amount = 999.00; // €999.00
      } else {
        amount = 999.00; // default monthly price for enterprise
      }
    } else if (planType === 'pro') {
      if (billingPeriod === 'annual') {
        amount = 199.99; // €199.99
      } else if (billingPeriod === 'monthly') {
        amount = 19.99; // €19.99
      } else {
        amount = 19.99; // default monthly price for pro
      }
    } else {
      // Default or other plans pricing
      amount = 299.00; // €299.00
    }
    return amount;
  };

  const amount = calculateAmount();

  useEffect(() => {
    console.log('CryptoPaymentForm rendered with plan:', planType, 'billing:', billingPeriod);
  }, [planType, billingPeriod]);

  const handleCryptoPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setQrCodeUrl(null);
    setCheckoutUrl(null);

    try {
      const merchantTradeNo = `ORD-${Date.now()}`;
      const res = await apiClient.post('/api/crypto/pay', {
        orderAmount: amount,
        currency: 'USDT',
        merchantTradeNo,
        productDetail: `RapidMind AI ${planType} Plan (${billingPeriod})`,
      });

      const data = res.data?.data;
      if (data?.checkoutUrl && data?.qrcodeLink) {
        setCheckoutUrl(data.checkoutUrl);
        setQrCodeUrl(data.qrcodeLink);

        // Save subscription info to Firestore
        const user = auth.currentUser;
        if (user) {
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            plan: planType,
            billingPeriod: billingPeriod,
            planId: merchantTradeNo,
            subscribedAt: new Date(),
            paymentMethod: 'binance_pay',
            status: 'pending' // Will be updated when payment is confirmed
          }, { merge: true });
          console.log('Subscription info saved to Firestore');
        } else {
          console.warn('No authenticated user found to save subscription info');
        }

        // Redirect to payment success page with role selection
        const successUrl = `/payment-success?merchantTradeNo=${merchantTradeNo}&planType=${planType}&billingPeriod=${billingPeriod}`;
        window.location.href = successUrl;
      } else {
        alert('Failed to initiate crypto payment');
      }
    } catch (err) {
      console.error('Crypto payment error:', err);
      alert('Payment error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <form onSubmit={handleCryptoPayment} style={styles.form}>
        <h2 style={styles.title}>
          Paiement - {planType} ({billingPeriod})
        </h2>
        <img 
          src={img} 
          alt="Binance Pay" 
          style={styles.logo}
        />
        
        <div style={styles.amountDisplay}>
          <h3 style={styles.amountText}>Montant: {amount} USDT</h3>
          <p style={styles.planInfo}>Plan: {planType} - Paiement {billingPeriod}</p>
        </div>

        <button 
          type="submit" 
          style={styles.button} 
          disabled={loading}
        >
          {loading ? 'Traitement en cours...' : 'Payer avec Binance Pay'}
        </button>

        <button
          type="button"
          style={styles.cancelButton}
          onClick={() => window.history.back()}
          disabled={loading}
        >
          Retour
        </button>

        {qrCodeUrl && (
          <div style={styles.qrSection}>
            <h3 style={styles.qrTitle}>Scanner pour payer</h3>
            <img src={qrCodeUrl} alt="QR Code Binance" style={styles.qrCode} />
            <a 
              href={checkoutUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={styles.checkoutLink}
            >
              Ou cliquez ici pour payer directement →
            </a>
          </div>
        )}
      </form>
    </div>
  );
};

export default CryptoPaymentForm;

// Styles to match Stripe form
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.85)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: "20px",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    color: "#eee",
  },
  form: {
    backgroundColor: "#1e1e1e",
    padding: "30px",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  title: {
    marginBottom: "10px",
    fontSize: "1.5rem",
    color: "#eee",
    textAlign: "center",
  },
  logo: {
    width: '120px',
    height: 'auto',
    display: 'block',
    margin: '0 auto'
  },
  amountDisplay: {
    textAlign: 'center',
    padding: '15px',
    backgroundColor: '#2a2a2a',
    borderRadius: '8px',
    border: '1px solid #444'
  },
  amountText: {
    margin: '0 0 8px 0',
    fontSize: '1.3rem',
    color: '#ff4081'
  },
  planInfo: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#ccc'
  },
  button: {
    padding: "12px",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "#ff4081",
    color: "#fff",
    fontSize: "1.1rem",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background 0.3s",
  },
  cancelButton: {
    padding: "12px",
    borderRadius: "6px",
    border: "none",
    fontSize: "1.1rem",
    backgroundColor: "#ff3b3b",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background 0.3s",
  },
  qrSection: {
    textAlign: 'center',
    padding: '15px',
    backgroundColor: '#2a2a2a',
    borderRadius: '8px',
    border: '1px solid #444'
  },
  qrTitle: {
    margin: '0 0 15px 0',
    fontSize: '1.1rem',
    color: '#eee'
  },
  qrCode: {
    width: '150px',
    height: '150px',
    margin: '0 auto 15px',
    display: 'block'
  },
  checkoutLink: {
    color: '#ff4081',
    textDecoration: 'none',
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
};
